// temporary eslint disable
/* eslint-disable */
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Status } from 'redux/types';
import { useHotSpot } from 'redux/reducers/hotSpot/hooks';
import { actions, FilesState } from 'redux/reducers/files';
import { useWorkSteps } from 'redux/reducers/worksteps';
import { useAnnotations } from 'redux/reducers/annotations';
import { ProjectState } from 'redux/reducers/project';
import { useProcedureFilesLoad } from 'hooks/useProcedureFilesLoad';
import { Flex, toast, Icon, Button } from '@cognite/cogs.js';
import { KeyValuePair, WorkStep, CogniteAnnotation, Phase } from 'types';
import { cookieServices, workstepsService } from 'services';
import { useDrawings } from 'hooks/useDrawings';
import debounce from 'lodash/debounce';
import {
  Annotation,
  TooltipAnchorPosition,
  ContainerConfig,
  ContainerType,
  ToolType,
  IdsByType,
  UnifiedViewerNode,
  UnifiedViewerEventListenerMap,
  UnifiedViewerEventType,
  ToolConfig,
  AnnotationType,
  LineType,
  PolylineAnnotation,
} from '@cognite/unified-file-viewer';
import { AnnotationModel, FileLink, IdEitherWithInstance } from '@cognite/sdk';
import { useColor } from 'react-color-palette';
import { v4 as uuidv4 } from 'uuid';
import { AnnotationForm } from '../AnnotationForm';
import { StepBox } from './StepBox';
import SideDrawer from './SideDrawer';
import { showFile } from './ShowFile';
import {
  AnnotationFormWrapper,
  FilePreviewWrapper,
  FileRefreshButton,
  Loader,
  LoaderWrapper,
  FileQualityButtons,
} from './elements';
import { useSideDrawerFilesActions } from './hooks/useSideDrawerFilesActions';
import { usePnidDrawing } from './hooks/usePnidDrawing';
import { usePnidZoom, ViewerQuality } from './hooks/usePnidZoom';
import { PnidMiniWidget } from './PnidMiniWidget';
import { getClient } from '../../../../config/cognitesdk';
import { HotSpotMenu } from '../HotSpotMenu/HotSpotMenu';
import UnifiedFileViewer from '../../../../components/UnifiedFileViewer/UnifiedFileViewer';
import { ToolConfigByToolType } from '../../../../components/UnifiedFileViewer/types';
import { ToolbarToolOption } from '../../../../components/UnifiedFileViewer/components/Toolbar';
import PolylineToolBar from 'components/UnifiedFileViewer/components/PolylineToolBar/PolylineToolBar';
import { actions as annotationActions } from 'redux/reducers/annotations/annotationsReducer';
import { rgbToRgba } from 'utils/colors';
import { logger } from 'utils/logger';
import {
  hasNumericCustomOffset,
  hasNumericGhostCoordinates,
  isGhostAnnotationId,
  isTouchPointerEvent,
} from './utils/annotationGuard';
import {
  createViewerRefHandler,
  ViewerState,
} from './utils/viewerZoomListener';
import { shouldRenderStepBox } from './utils/shouldRenderStepBox';
// import { AuthState } from 'redux/reducers/auth';

export type PnidPreviewProps = {
  fileIds: number[];
  newProject: boolean;
  annotations?: any[];
  workStepsFileIds: number[];
  setWorkStepsFileIds: Function;
  viewOnly: boolean;
  showWorkSteps?: boolean;
  tool: ToolConfig;
  setTool: (nextTool: ToolType | ToolConfig) => void;
  toolConfigByToolType: Partial<ToolConfigByToolType>;
  tools: ToolbarToolOption[];
};

const mergeContainers = (
  containerA: ContainerConfig[],
  containerB: ContainerConfig[]
) => {
  const mergedMap = new Map();

  [...containerA, ...containerB].forEach((obj) => {
    const existingObj = mergedMap.get(obj.id);
    if (existingObj) {
      mergedMap.set(obj.id, {
        ...existingObj,
        ...obj,
      });
    } else {
      mergedMap.set(obj.id, obj);
    }
  });

  return Array.from(mergedMap.values());
};

export const FilePreview = (props: PnidPreviewProps) => {
  const {
    fileIds,
    viewOnly,
    showWorkSteps,
    tool,
    setTool,
    toolConfigByToolType: optionsByToolType,
  } = props;
  const dispatch = useDispatch();
  // const metrics = useMetrics(METRICS_AREA.PNID_LOAD);
  const { selectHotSpot, activeHotSpot, clearActiveHotSpot } = useHotSpot();
  const { project, newProject } = useSelector<RootState, ProjectState>(
    (state: RootState) => state.project
  );
  // const {user} = useSelector<RootState, AuthState>(
  //   (state: RootState) => state.auth
  // );

  const {
    active,
    workStepsLoaded,
    phases,
    reorderedWorkStepPosition,
    selectWorkStep,
    getFirstPnidId,
    countAllWorkSteps,
    fetchFileForActiveWorkstep,
    cancelActive,
    setWorkStepAnnotationCustomOffset,
    getWorkstepPairs,
  } = useWorkSteps();

  let {
    file,
    fileId,
    files,
    connectedFiles,
    fetchFile,
    setFileId,
    onAddFile,
    onFileRemoved,
    onFileSelected,
    chooseStartingFile,
  } = useSideDrawerFilesActions();

  const [isReady, setIsReady] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { preloadFilesWithAnnotations } = useProcedureFilesLoad();
  const {
    fetchDrawingForPnid,
    saveDrawingForPnid,
    getEmptyDrawingContent,
    preloadDrawingsForProcedure,
  } = useDrawings();
  const [showLoader, setShowLoader] = useState(false);
  const [pnidLoadStartTime, setPnidLoadStartTime] = useState(0);

  const isExecutionView = project.status === 'execution';

  const quality = cookieServices.getFilePreviewQuality() || ViewerQuality.HIGH;
  const fileViewerQuality = useMemo(() => {
    // Returning undefined will make scale depending on the screen size
    if (!isExecutionView) {
      // Always show the highest quality for editing procedure
      return undefined;
    }
    if (quality === ViewerQuality.LOW) {
      return 1;
    }
    if (quality === ViewerQuality.MEDIUM) {
      return 3;
    }
    return undefined;
  }, [quality, isExecutionView]);

  const {
    editMode,
    newAnnotation,
    fileColors,
    fetchAnnotations,
    createNewAnnotation,
    highlightedAnnotations,
    setAnnotationCustomOffset,
  } = useAnnotations();

  const { curZoomedAnnotation, zoomOnAnnotation, setZoomedAnnotation } =
    usePnidZoom(quality);

  const numberOfFiles = Number(fileIds?.length);
  const noWorksteps = countAllWorkSteps() === 0;

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [ghostPositionBySourceId, setGhostPositionBySourceId] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const [activeAnnotation, setActiveAnnotation] = useState<AnnotationModel>();
  const [clickedId, setClickedId] = useState<number>(0);
  const [polylineColor, setPolylineColor] = useColor('#96df96');
  const [polylineSize, setPolylineSize] = useState(12);
  let [copyoptionsByToolType, setCopyoptionsByToolType] = useState<ToolConfig>(
    {} as ToolConfig
  );
  const annotationSet = useSelector<RootState>(
    (state: RootState) => state.annotations.annotations[file.id]
  ) as Annotation[];

  const [urls, setUrls] = useState<
    (FileLink & IdEitherWithInstance)[] | undefined
  >();
  const [polylineNodes, setPolylineNodes] = useState<UnifiedViewerNode[]>([]);
  const resetPolylineNodes = useCallback(() => {
    setPolylineNodes([]);
  }, []);
  const [container, setContainer] = useState<ContainerConfig[]>([]);
  const client = getClient();
  const viewerStateRef = useRef<ViewerState>({
    viewer: null,
    cleanup: null,
  });
  const selectGhostAnnotationRef = useRef<(annotationId: string) => void>(
    () => {}
  );

  const selectViewerAnnotations = (annotationIds: string[] = []) => {
    viewerStateRef.current.viewer?.selectByIds({
      containerIds: [],
      annotationIds,
    });
  };

  selectGhostAnnotationRef.current = (annotationId: string) =>
    selectViewerAnnotations([annotationId]);

  useEffect(() => {
    return () => {
      viewerStateRef.current.cleanup?.();
    };
  }, []);

  const handleViewerRef = useCallback(
    createViewerRefHandler(viewerStateRef),
    []
  );

  useEffect(() => {
    if (active.editable && !active.workStep?.stepAction) {
      selectViewerAnnotations();
    }
  }, [active.editable]);

  useEffect(() => {
    if (tool.type === 'polyline') {
      const obj = {
        type: ToolType.POLYLINE,
        fill: `${polylineColor.hex}`,
        stroke: `${polylineColor.hex}`,
        strokeWidth: polylineSize,
        opacity: 0.6,
      };
      setCopyoptionsByToolType(obj as ToolConfig);
    }
  }, [polylineColor, polylineSize, tool.type]);

  const annotationData = useMemo<Annotation[]>(
    () =>
      annotations.map((annotation) => ({
        ...annotation,
        onClick: (e: any) => {
          if (active) {
            e.cancelBubble = true;
          }
        },
      })),
    [annotations, active]
  );

  useEffect(() => {
    if (activeHotSpot.annotation) {
      const annotation = activeHotSpot.annotation;
      setClickedId(annotation.id);
      setActiveAnnotation(annotation);
    }
  }, [activeHotSpot]);

  useEffect(() => {
    if (fileId !== undefined) {
      const annotations = highlightedAnnotations(file.id);
      if (annotations) {
        // setting to annotations type to 'any' temporarily
        const textRegions = annotations.map((annotation: any) => {
          if (annotation.data.textRegion) {
            return {
              id: String(annotation.id),
              containerId: String(fileId),
              type: 'rectangleAnnotation',
              x: annotation.data.textRegion.xMin,
              y: annotation.data.textRegion.yMin,
              width:
                annotation.data.textRegion.xMax -
                annotation.data.textRegion.xMin,
              height:
                annotation.data.textRegion.yMax -
                annotation.data.textRegion.yMin,
              style: {
                ...(annotation.mark &&
                  'strokeColor' in annotation.mark && {
                  stroke: checkIfOldAnnotation(annotation, active)
                    ? annotation.mark.strokeColor
                    : rgbToRgba(annotation.mark.strokeColor as string, 0.3),
                }),
                ...(annotation.mark &&
                  'strokeWidth' in annotation.mark && {
                  strokeWidth: annotation.mark.strokeWidth,
                }),
                fill: rgbToRgba(annotation.mark.backgroundColor as string, 0.3),
              },
              metadata: {
                annotation,
              },
              isDraggable: false,
              isResizable: false,
            } as Annotation;
          }
        });
        setAnnotations(textRegions as Annotation[]);
      }
    }
  }, [fileId, file, annotationSet, active]);

  const checkIfOldAnnotation = (annotation: any, active: any) => {
    const isOldAnnotation =
      'oldAnnotationId' in annotation?.data &&
      annotation?.data.oldAnnotationId &&
      annotation?.creatingUser !== 'currentLoginUser';
    const isStepItemAvailable = active.workStep && active.workStep.stepItem;

    if (isOldAnnotation && isStepItemAvailable) {
      return (
        active.workStep.stepItem.annotation?.id ===
        parseInt(annotation?.data.oldAnnotationId, 10) ||
        [
          active.workStep?.stepItem?.relativeRef?.annotation?.id,
          active.workStep?.stepItem?.line?.annotation?.id,
        ].includes(parseInt(annotation?.data.oldAnnotationId, 10))
      );
    } else if (isStepItemAvailable) {
      return (
        active.workStep.stepItem.annotation?.id === annotation.id ||
        [
          active.workStep?.stepItem?.relativeRef?.annotation?.id,
          active.workStep?.stepItem?.line?.annotation?.id,
        ].includes(annotation.id)
      );
    }
  };

  useEffect(() => {
    let isStale = false;

    const fetchUrls = async () => {
      if (!file.id) {
        setUrls(undefined);
        return;
      }

      setUrls(undefined);
      try {
        const urlsResponse = await client.files.getDownloadUrls([
          { id: file.id },
        ]);
        if (isStale) {
          return;
        }
        setUrls(urlsResponse);
      } catch (error) {
        if (isStale) {
          return;
        }
        console.error('Failed to fetch download URLs:', error);
      }
    };

    fetchUrls();
    return () => {
      isStale = true;
    };
  }, [client, file.id]);

  const CONTAINERS: ContainerConfig[] | any = useMemo(
    () =>
      urls
        ?.filter((url) => 'id' in url)
        .map((url) => ({
          id: String(url.id),
          type: ContainerType.DOCUMENT,
          url: url.downloadUrl.toString(),
          maxWidth: 5000,
          maxHeight: 5000,
        })),
    [urls]
  );

  const fetchDrawing = useCallback(
    (fileIdNumber: number) => {
      return fetchDrawingForPnid(
        fileIdNumber,
        project.id as string,
        project.pnIdsDrawings as KeyValuePair
      );
    },
    [
      fetchDrawingForPnid,
      getEmptyDrawingContent,
      project.id,
      project.pnIdsDrawings,
    ]
  );

  const getSavedDrawingId = (): string | undefined => {
    if (file.id == null || project.pnIdsDrawings == null) {
      return undefined;
    }

    const drawingId = project.pnIdsDrawings[file.id];
    if (drawingId == null) {
      return undefined;
    }

    return String(drawingId);
  };

  const savedDrawingId = getSavedDrawingId();

  const { drawing, drawingFileId, setDrawing } = usePnidDrawing({
    fileId: file.id,
    drawingId: savedDrawingId,
    fetchDrawing,
    getEmptyDrawingContent,
    onReset: resetPolylineNodes,
  });

  useEffect(() => {
    if (!file.id || drawingFileId !== file.id || !drawing) {
      return;
    }

    setPolylineNodes(FlatDrawingToUFVNode(drawing));
  }, [drawing, drawingFileId, file.id]);

  const { status: statusFiles } = useSelector<RootState, FilesState>(
    (state) => state.files
  );
  const statusAnnotations = useSelector<RootState>(
    (state: RootState) => state.annotations.status
  ) as Status;
  const statusProcedureAnnotations = useSelector<RootState>(
    (state: RootState) => state.annotations.annotationsLoadedStatus
  ) as Status;

  const isFileFullyLoaded =
    statusFiles === Status.success &&
    statusAnnotations === Status.success &&
    statusProcedureAnnotations === Status.success &&
    isReady;
  const isPnidReady =
    !file.id ||
    Boolean(
      urls?.some((url) => 'id' in url && Number(url.id) === Number(file.id))
    );

  // show sidebar if no worksteps and have files
  useEffect(() => {
    if (numberOfFiles > 1 && workStepsLoaded && isReady && noWorksteps) {
      setIsDrawerOpen(true);
    }
    // eslint-disable-next-line
  }, [numberOfFiles, workStepsLoaded, isReady, noWorksteps]);

  // Listen for file change and set the local fileId vairable
  // Internally the fileId is used to detect changes
  useEffect(() => {
    if (file.id !== fileId) {
      setFileId(file.id);
    }
  }, [file.id, fileId, setFileId]);

  useEffect(() => {
    setContainer([]);
  }, [fileId]);

  // When the active workstep is changed
  // zoom the P&ID to the active ws
  useEffect(() => {
    if (!showLoader && active.workStep && project.status !== 'compilation') {
      zoomOnAnnotation(active);
    }
    // eslint-disable-next-line
  }, [active, showLoader, zoomOnAnnotation]);

  // Init hook, first load, it preloads the P&IDs, annotations...etc.
  useEffect(() => {
    if (
      workStepsLoaded &&
      statusProcedureAnnotations === Status.idle &&
      !isReady
    ) {
      setDrawing(getEmptyDrawingContent());
      preloadFilesWithAnnotations(project, phases, newProject, fileIds);
      preloadDrawingsForProcedure(project);
    }
    // eslint-disable-next-line
  }, [
    isReady,
    workStepsLoaded,
    statusProcedureAnnotations,
    props.newProject,
    phases,
    project,
    preloadFilesWithAnnotations,
    preloadDrawingsForProcedure,
    getEmptyDrawingContent,
  ]);

  // After preload is done, it selects the first P&ID from ws
  useEffect(() => {
    if (
      workStepsLoaded &&
      statusProcedureAnnotations === Status.success &&
      !isReady
    ) {
      // Read out if user has been working on different p&id and reloads
      const cookieInfo = cookieServices.getFilePreviewInfo();
      if (cookieInfo) {
        if (!fileIds.includes(cookieInfo.id)) {
          dispatch(actions.connectFile(cookieInfo.id));
          onAddFile(cookieInfo);
        } else {
          dispatch(actions.setActiveFile(cookieInfo.id));
          setFileId(cookieInfo.id);
        }
        // Remove cookie after reload so user gets initial p&id on next visit
        cookieServices.removeFilePreviewInfo();
      } else {
        const pnidFromWorsteps = getFirstPnidId();
        if (pnidFromWorsteps) {
          dispatch(actions.setActiveFile(pnidFromWorsteps));
        } else if (!pnidFromWorsteps && numberOfFiles === 1) {
          setFileId(fileIds[0]);
          dispatch(actions.setActiveFile(fileIds[0]));
        }
      }

      setIsReady(true);
    }
  }, [
    dispatch,
    isReady,
    workStepsLoaded,
    statusProcedureAnnotations,
    props.newProject,
    numberOfFiles,
    fileIds,
    setFileId,
    fetchDrawing,
    onAddFile,
  ]);

  // Watch the active ws, when it is changed, the new file is loaded
  useEffect(() => {
    if (
      active.workStep?.id &&
      active.workStep.stepItem &&
      active.workStep.stepItem.annotation
    ) {
      const workStepFileId =
        active.workStep.stepItem?.annotation?.annotatedResourceId;
      if (fileId !== workStepFileId) {
        // commented temporarily
        // setShowLoader(true);
        setDrawing(getEmptyDrawingContent());

        const timeoutInMsToRedrawUI = project.status !== 'execution' ? 800 : 0;
        setPnidLoadStartTime(window.performance.now());
        // timeout is set to wait firt ui actions to finish
        // to avoid poor rendering performance
        // triggering setActiveFile will trigger other events
        // in order file to be fetched with all other things
        setTimeout(() => {
          fetchFileForActiveWorkstep(active.workStep as WorkStep);
        }, timeoutInMsToRedrawUI);
      }
    }

    // eslint-disable-next-line
  }, [active.workStep]);

  useEffect(() => {
    if (statusProcedureAnnotations === Status.failed) {
      toast.error(
        <div>
          Annotations for this procedure can not be fetched. Please try to
          refresh the page.
        </div>
      );
    }

    // eslint-disable-next-line
  }, [statusProcedureAnnotations]);

  useEffect(() => {
    const connectedWorkstepsFileIds =
      workstepsService.getWorkstepsFileIds(phases);
    if (connectedWorkstepsFileIds.length !== props.workStepsFileIds.length) {
      props.setWorkStepsFileIds(connectedWorkstepsFileIds);
    }

    // eslint-disable-next-line
  }, [connectedFiles, phases]);

  const onArrowBoxMove = useCallback(
    (fileId: number, annotationId: number, x: number, y: number) => {
      setAnnotationCustomOffset(fileId, annotationId, x, y);
      setWorkStepAnnotationCustomOffset(annotationId, x, y);
    },
    [file.id, setAnnotationCustomOffset, setWorkStepAnnotationCustomOffset]
  );

  const onDrawingSaved = useCallback(
    (newDrawData: string) => {
      if (
        newDrawData &&
        newDrawData.length > 0 &&
        drawing !== String(newDrawData)
      ) {
        const projectId = project.id || 'undefined';
        saveDrawingForPnid(file.id, projectId, String(newDrawData));
      }
    },
    [drawing, file.id, project.id, saveDrawingForPnid]
  );

  const onAnnotationSelected = debounce((ids: IdsByType) => {
    const selectedAnnotationId = ids.annotationIds[0];
    const selectedAnnotation = annotationData.filter((annotation) => {
      if ('metadata' in annotation && 'annotation' in annotation.metadata)
        return (
          annotation.metadata.annotation.id === parseInt(selectedAnnotationId)
        );
    });
    if (selectedAnnotation.length > 0 && 'metadata' in selectedAnnotation[0]) {
      const annotation = selectedAnnotation[0].metadata?.annotation;
      const isFile = annotation.data.linkedResourceType === 'file';
      const connectedFileId = annotation?.data.linkedResourceInternalId;
      if (isFile && annotation?.data.linkedResourceInternalId) {
        // setShowLoader(true); TODO: temporary comment. Might be removed later
        setZoomedAnnotation(undefined);
        cancelActive();
        if (connectedFiles.includes(connectedFileId)) {
          dispatch(actions.setActiveFile(connectedFileId));
        } else {
          dispatch(
            actions.connectFile(annotation?.data.linkedResourceInternalId)
          );
          fetchFile(connectedFileId);
          const needsMigration =
            project.needsMigration && project.needsMigration === true;
          fetchAnnotations(connectedFileId, phases, needsMigration);
        }
      } else {
        selectHotSpot(annotation);
      }
    }
  }, 200);

  const isOldAnnotation = (annotation: Annotation): string => {
    if (
      annotation.metadata &&
      'annotation' in annotation.metadata &&
      'data' in annotation.metadata.annotation &&
      'oldAnnotationId' in annotation.metadata.annotation.data &&
      annotation?.metadata?.annotation?.data?.oldAnnotationId
    ) {
      if (
        annotation?.metadata?.annotation?.creatingUser === 'currentLoginUser'
      ) {
        return annotation.id;
      } else if (
        checkWorkstepWithAnnotationId(phases, parseInt(annotation.id))
      ) {
        return annotation.id;
      }
      return annotation.metadata.annotation.data.oldAnnotationId;
    } else {
      return annotation.id;
    }
  };

  const checkWorkstepWithAnnotationId = (
    phases: Phase[],
    targetAnnotationId: number
  ) => {
    for (const phase of phases) {
      for (const workstep of phase.workSteps) {
        if (
          workstep?.stepItem?.annotation &&
          workstep?.stepItem?.annotation?.id === targetAnnotationId
        ) {
          return true;
        }
      }
    }
  };

  const renderStepBox = (annotation: any) => {
    const workSteps =
      workStepsByAnnotationId.get(isOldAnnotation(annotation)) || [];

    if (workSteps.length) {
      const workstepPairs = getWorkstepPairs(workSteps);
      return (
        <StepBox
          active={active.workStep}
          workStepsPairs={workstepPairs}
          workSteps={workSteps}
          onSelected={selectWorkStep}
        />
      );
    }
    return <></>;
  };

  const renderStepBoxConditionally = (annotation: Annotation) =>
    shouldRenderStepBox(annotation, active, showWorkSteps) ? (
      renderStepBox(annotation)
    ) : (
      <></>
    );

  useMemo(
    () => ({
      onCreate: (annotation: CogniteAnnotation) => {
        createNewAnnotation(annotation, file);
        return false;
      },
      onUpdate: (_annotation: CogniteAnnotation) => {
        return false;
      },
    }),
    [createNewAnnotation, file]
  );

  const setQuality = (qualityValue: ViewerQuality) => {
    cookieServices.setFilePreviewQuality(qualityValue);
    window.location.reload();
  };

  const onStageClick = (e: any) => {
    if (e.justDragged) {
      return;
    }

    const targetId = e.target?.id?.() ?? e.target?.attrs?.id;
    if (targetId && isGhostAnnotationId(String(targetId))) {
      return;
    }

    selectViewerAnnotations();

    if ('workStep' in active) {
      cancelActive();
    }
    clearActiveHotSpot();
  };

  const annotationById = useMemo(() => {
    return new Map(
      annotationData.map((annotation) => [annotation.id, annotation])
    );
  }, [annotationData]);

  const {
    workStepsByAnnotationId,
    sourceAnnotationsForGhosts,
    persistedGhostPositionBySourceId,
  } = useMemo(() => {
    const nextWorkStepsByAnnotationId = new Map<string, WorkStep[]>();
    const nextPersistedGhostPositionBySourceId: Record<
      string,
      { x: number; y: number }
    > = {};

    phases.forEach((phase) => {
      phase.workSteps.forEach((workStep) => {
        if (workStep.isDraft || workStep.workStepType === 'missing') {
          return;
        }
        const annotation = workStep.stepItem?.annotation;
        if (!annotation?.id) {
          return;
        }

        const key = String(annotation.id);
        const existing = nextWorkStepsByAnnotationId.get(key);
        if (existing) {
          existing.push(workStep);
        } else {
          nextWorkStepsByAnnotationId.set(key, [workStep]);
        }

        if (
          !nextPersistedGhostPositionBySourceId[key] &&
          hasNumericCustomOffset(annotation)
        ) {
          nextPersistedGhostPositionBySourceId[key] = {
            x: annotation.customOffset.x,
            y: annotation.customOffset.y,
          };
        }
      });
    });

    const nextSourceAnnotationsForGhosts = Array.from(
      nextWorkStepsByAnnotationId.keys()
    )
      .map((annotationId) => annotationById.get(annotationId))
      .filter(Boolean) as Annotation[];

    return {
      workStepsByAnnotationId: nextWorkStepsByAnnotationId,
      sourceAnnotationsForGhosts: nextSourceAnnotationsForGhosts,
      persistedGhostPositionBySourceId: nextPersistedGhostPositionBySourceId,
    };
  }, [phases, annotationById]);

  const getAnnotationAnchor = (annotation: Annotation) => {
    const anyAnnotation = annotation as any;
    if (
      'x' in anyAnnotation &&
      'y' in anyAnnotation &&
      typeof anyAnnotation.x === 'number' &&
      typeof anyAnnotation.y === 'number'
    ) {
      return {
        x: anyAnnotation.x,
        y: anyAnnotation.y,
      };
    }
    return { x: 0.5, y: 0.5 };
  };

  const getDefaultGhostPosition = (annotation: Annotation) => {
    const anchor = getAnnotationAnchor(annotation);
    return {
      x: Math.min(anchor.x + 0.1, 0.95),
      y: Math.max(anchor.y - 0.08, 0.02),
    };
  };

  useEffect(() => {
    setGhostPositionBySourceId({});
  }, [file.id]);

  useEffect(() => {
    setGhostPositionBySourceId((prev) => {
      const next: Record<string, { x: number; y: number }> = {};
      let changed = false;

      sourceAnnotationsForGhosts.forEach((annotation) => {
        const value =
          prev[annotation.id] ||
          persistedGhostPositionBySourceId[annotation.id] ||
          getDefaultGhostPosition(annotation);
        next[annotation.id] = value;
        /*
          Detect whether this ghost's position is new or actually moved.
          `value` may come from prev (unchanged), persisted data, or a default.
          If we always returned `next`, React would treat the state as updated on every effect run, even when x/y are identical,
          and rebuild ghostAnnotations / connections for no reason (e.g. when sourceAnnotationsForGhosts gets a new array identity with the same items).
        */
        const previousValue = prev[annotation.id];
        if (
          !previousValue ||
          previousValue.x !== value.x ||
          previousValue.y !== value.y
        ) {
          changed = true;
        }
      });
      /*
        Ghost removals aren't visited by the loop above, so per-id x/y checks miss them.
        If the ghost count changed, mark `changed` to true, so we return `next` and drop stale keys.
      */
      if (Object.keys(prev).length !== sourceAnnotationsForGhosts.length) {
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [sourceAnnotationsForGhosts, persistedGhostPositionBySourceId]);

  const ghostAnnotations: Annotation[] = useMemo(() => {
    return sourceAnnotationsForGhosts.map((sourceAnnotation) => {
      const ghostPosition =
        ghostPositionBySourceId[sourceAnnotation.id] ||
        getDefaultGhostPosition(sourceAnnotation);
      return {
        type: AnnotationType.ELLIPSE,
        id: `ghost-${sourceAnnotation.id}`,
        containerId: String(file.id),
        x: ghostPosition.x,
        y: ghostPosition.y,
        radius: 0.008,
        isDraggable: true,
        isResizable: false,
        style: {
          fill: 'rgba(238, 14, 14, 0.5)',
        },
        onMouseDown: (event, viewerAnnotation) => {
          if (!isTouchPointerEvent(event.evt)) {
            return;
          }
          event.cancelBubble = true;
          selectGhostAnnotationRef.current(viewerAnnotation.id);
        },
        onClick: (event) => {
          event.cancelBubble = true;
        },
      };
    });
  }, [sourceAnnotationsForGhosts, ghostPositionBySourceId, file.id]);

  const ghostConnections: PolylineAnnotation[] = useMemo(() => {
    return sourceAnnotationsForGhosts.map((sourceAnnotation) => ({
      id: `connection-${sourceAnnotation.id}`,
      type: AnnotationType.POLYLINE,
      fromId: sourceAnnotation.id,
      toId: `ghost-${sourceAnnotation.id}`,
      style: {
        stroke: 'rgba(145, 18, 9, 0.77)',
        strokeWidth: 8,
        lineType: LineType.ELBOWED,
      },
      isSelectable: false,
      isDraggable: false,
      isResizable: false,
    }));
  }, [sourceAnnotationsForGhosts]);

  const stepBoxes = useMemo(() => {
    return sourceAnnotationsForGhosts.map((sourceAnnotation) => ({
      id: `tooltip-${sourceAnnotation.id}`,
      targetIds: [`ghost-${sourceAnnotation.id}`],
      content: renderStepBoxConditionally(sourceAnnotation),
      anchorTo: TooltipAnchorPosition.TOP_CENTER,
    }));
  }, [
    sourceAnnotationsForGhosts,
    active,
    showWorkSteps,
    reorderedWorkStepPosition,
    workStepsByAnnotationId,
  ]);

  const hotSpotMenu = useMemo(() => {
    if (clickedId === undefined) {
      return [];
    }

    if (activeAnnotation)
      return [
        {
          targetIds: [clickedId.toString()],
          content: <HotSpotMenu annotation={activeAnnotation} />,
          anchorTo: TooltipAnchorPosition.TOP_LEFT,
        },
      ];
  }, [clickedId, activeAnnotation]);

  const toolTipItems = [...stepBoxes, ...(hotSpotMenu || [])];

  const undoLastPolyline = () => {
    if (polylineNodes.length > 1) {
      const lastNode = polylineNodes[polylineNodes.length - 1];
      if (
        'vertices' in lastNode &&
        lastNode.vertices &&
        lastNode.vertices?.length > 0
      ) {
        const updatedVertices = lastNode.vertices.slice(0, -1);
        const updatedPolylineNodes = [
          ...polylineNodes.slice(0, -1),
          ...(updatedVertices.length > 1
            ? [{ ...lastNode, vertices: updatedVertices }]
            : []),
        ];
        setPolylineNodes(updatedPolylineNodes);
      }
    } else if (polylineNodes.length === 1 && 'vertices' in polylineNodes[0]) {
      const updatedVertices = polylineNodes[0].vertices?.slice(0, -1);
      const updatedPolylineNodes = [
        { ...polylineNodes[0], vertices: updatedVertices },
      ];
      setPolylineNodes(
        updatedVertices && updatedVertices?.length > 1
          ? updatedPolylineNodes
          : []
      );
    }
  };

  const eraseAllPolylines = () => {
    setPolylineNodes([]);
  };

  const UFVNodeToFlatDrawing = (annotations: UnifiedViewerNode[]) => {
    const drawingCoordinates: any[] = [];
    annotations.forEach((annotation) => {
      if ('vertices' in annotation) {
        const lineObj = {
          points: annotation.vertices,
          brushColor:
            annotation.style?.stroke !== undefined
              ? annotation.style?.stroke?.startsWith('rgba(')
                ? annotation.style?.stroke
                : rgbToRgba(
                  annotation.style?.stroke!,
                  annotation.style?.opacity!
                )
              : 'rgba(150, 223, 150, 0.6)',
          brushRadius: annotation.style?.strokeWidth,
        };
        drawingCoordinates.push(lineObj);
      }
    });

    return drawingCoordinates;
  };

  const FlatDrawingToUFVNode = (drawing: string) => {
    try {
      const drawingParsed = JSON.parse(drawing);

      if (
        !drawingParsed ||
        typeof drawingParsed !== 'object' ||
        !Array.isArray(drawingParsed.lines)
      ) {
        return [];
      }

      return drawingParsed.lines.map((item: any) => {
        return {
          id: uuidv4(),
          type: 'polylineAnnotation',
          containerId: String(file.id),
          isSelectable: false,
          isDraggable: false,
          isResizable: false,
          vertices: item.points,
          startEndType: 'none',
          endEndType: 'none',
          style: {
            strokeWidth:
              parseInt(item.brushRadius) > 40
                ? 12
                : parseInt(item.brushRadius),
            stroke: item.brushColor,
            opacity: 0.9,
          },
        } as UnifiedViewerNode;
      });
    } catch (error) {
      logger(error, 'Failed to parse drawing JSON');
      return [];
    }
  };

  const savePolylineAnnotations = (annotations: UnifiedViewerNode[]) => {
    const lines =
      annotations.length > 0 ? UFVNodeToFlatDrawing(annotations) : [];

    const transformedDrawing = {
      lines: lines,
      width: '100%',
      height: '100%',
    };
    onDrawingSaved(JSON.stringify(transformedDrawing));
  };

  const onUpdateAnnotations: UnifiedViewerEventListenerMap[UnifiedViewerEventType.ON_UPDATE_REQUEST] =
    useCallback(
      (updatedContainersAndAnnotations) => {
        let newAnnotations;
        const { containers, annotations } = updatedContainersAndAnnotations;

        if (containers.length > 0) {
          setContainer(containers);
        }

        if (annotations.length > 0 && tool.type === 'rectangle') {
          newAnnotations = annotations.map((annotation: any) => ({
            annotatedResourceType: 'file',
            annotatedResourceId: Number(annotation.containerId),
            annotationType: 'isoplan.IsoPlanAnnotation',
            creatingApp: 'isoplan',
            creatingAppVersion: '1.2.0',
            creatingUser: 'currentLoginUser',
            data: {
              textRegion: {
                xMax: annotation.x + annotation.width,
                xMin: annotation.x,
                yMin: annotation.y,
                yMax: annotation.y + annotation.height,
              },
            },
            status: 'approved',
          })) as CogniteAnnotation[];

          dispatch(
            annotationActions.setNewAnnotation({
              newAnnotation: newAnnotations[0],
            })
          );
        } else if (annotations.length > 0 && tool.type === 'polyline') {
          const polylineAnnotation = {
            ...annotations[0],
            containerId: String(file.id),
            style: {
              strokeWidth:
                annotations[0].style &&
                  'strokeWidth' in annotations[0].style &&
                  annotations[0].style.strokeWidth
                  ? annotations[0].style.strokeWidth
                  : 12,
              stroke:
                annotations[0].style &&
                  'stroke' in annotations[0].style &&
                  annotations[0].style.stroke
                  ? annotations[0].style.stroke
                  : 'rgb(150, 223, 150)',
              opacity: 0.6,
            },
          };

          setPolylineNodes((prevNodes) => {
            if (!prevNodes || prevNodes.length === 0) {
              return [polylineAnnotation] as UnifiedViewerNode[];
            }

            return [
              ...prevNodes.filter(
                (node: any) => node.id !== polylineAnnotation.id
              ),
              polylineAnnotation as UnifiedViewerNode,
            ];
          });
        } else if (annotations.length > 0 && containers.length === 0) {
          const annotationUpdates: Record<string, { x: number; y: number }> =
            {};
          annotations.forEach((annotation) => {
            if (!hasNumericGhostCoordinates(annotation)) {
              return;
            }
            const sourceId = annotation.id.slice('ghost-'.length);
            const sourceIdNum = Number(sourceId);
            if (!Number.isFinite(sourceIdNum)) {
              return;
            }
            annotationUpdates[sourceId] = { x: annotation.x, y: annotation.y };
            onArrowBoxMove(file.id, sourceIdNum, annotation.x, annotation.y);
          });

          setGhostPositionBySourceId((prev) => ({
            ...prev,
            ...annotationUpdates,
          }));
        }
      },
      [tool.type, file.id, onArrowBoxMove, dispatch]
    );

  const handleFocus = () => {
    setTool(ToolType.SELECT);
  };

  const nodesCollection = useMemo(() => {

    const combinedAnnotations = [
      ...annotationData,
      ...ghostAnnotations,
      ...ghostConnections,
    ];
    let mergedContainers: ContainerConfig[] = [];
    if (container.length > 0 && CONTAINERS && CONTAINERS.length > 0) {
      mergedContainers = mergeContainers(CONTAINERS, container);
    } else if (CONTAINERS && CONTAINERS.length > 0) {
      mergedContainers = CONTAINERS;
    }
    return [...mergedContainers, ...combinedAnnotations, ...polylineNodes];
  }, [
    annotationData,
    ghostAnnotations,
    ghostConnections,
    container,
    CONTAINERS,
    polylineNodes,
  ]);

  const FileViewer = (
    <>
      <UnifiedFileViewer
        key={fileId ?? 'no-file-selected'}
        applicationId="my-application"
        shouldUseAdaptiveRendering={true}
        nodes={nodesCollection}
        tool={
          tool.type === 'polyline' &&
            Object.keys(copyoptionsByToolType).length > 0
            ? copyoptionsByToolType
            : tool
        }
        tooltips={toolTipItems}
        onClick={onStageClick}
        shouldAllowDragDrop={true}
        onUpdateRequest={onUpdateAnnotations}
        onSelect={onAnnotationSelected}
        setRef={handleViewerRef}
      />
    </>
  );

  const filePreview = (
    <>
      {showFile({
        fileId,
        fileIds,
        isFileFullyLoaded: isFileFullyLoaded && isPnidReady,
        FileViewer,
      })}
      {newAnnotation?.annotation &&
        'textRegion' in newAnnotation.annotation.data && (
          <AnnotationFormWrapper>
            <AnnotationForm
              annotation={newAnnotation?.annotation}
              oldAnnotation={newAnnotation?.oldAnnotation}
              procedureNeedsMigration={project.needsMigration}
              onFocus={handleFocus}
            />
          </AnnotationFormWrapper>
        )}
      {tool.type === 'polyline' && (
        <PolylineToolBar
          color={polylineColor}
          polylineSize={polylineSize}
          polylineNodes={polylineNodes}
          setPolylineSize={setPolylineSize}
          setColor={setPolylineColor}
          eraseAllPolylines={eraseAllPolylines}
          undoLastPolyline={undoLastPolyline}
          savePolylineAnnotations={savePolylineAnnotations}
        />
      )}
    </>
  );

  return (
    <FilePreviewWrapper
      data-testid="pnidPreview"
      data-activefileid={fileId || ''}
    >
      <LoaderWrapper
        data-testid="pnid-main-loader"
        className={showLoader ? 'visible' : ''}
      >
        <Loader />
      </LoaderWrapper>
      {isExecutionView ? (
        <>
          <FileRefreshButton
            type="secondary"
            onClick={() => {
              cookieServices.setFilePreviewInfo(file);
              window.location.reload();
            }}
          >
            <Icon
              type="Warning"
              style={{
                marginRight: '10px',
              }}
            />
            Reload
          </FileRefreshButton>
          <FileQualityButtons>
            <Button
              type={quality === ViewerQuality.LOW ? 'primary' : 'secondary'}
              onClick={() => setQuality(ViewerQuality.LOW)}
            >
              Low
            </Button>
            <Button
              type={quality === ViewerQuality.MEDIUM ? 'primary' : 'secondary'}
              onClick={() => setQuality(ViewerQuality.MEDIUM)}
            >
              Med
            </Button>
            <Button
              type={quality === ViewerQuality.HIGH ? 'primary' : 'secondary'}
              onClick={() => setQuality(ViewerQuality.HIGH)}
            >
              High
            </Button>
          </FileQualityButtons>
        </>
      ) : null}
      {filePreview}
      {project.status !== 'execution' && (
        <SideDrawer
          viewOnly={viewOnly}
          fileId={fileId}
          fileIds={fileIds}
          workStepsFileIds={props.workStepsFileIds}
          files={files}
          activeFile={file}
          fileColors={fileColors}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          onFileSelect={onFileSelected}
          onChooseStartingFile={chooseStartingFile}
          onFileRemove={onFileRemoved}
          onAddFile={onAddFile}
        />
      )}
    </FilePreviewWrapper>
  );
};

const FilePreviewWithProvider = (props: PnidPreviewProps) => {
  return (
    <Flex direction="column" style={{ width: '100%' }}>
      <PnidMiniWidget />
      <FilePreview {...props} />
    </Flex>
  );
};

export { FilePreviewWithProvider as PnidPreview };
